import { ActionCategory } from "../../generated/core/v1/actions";
import type { Date as PDate } from "../../generated/core/v1/date";
import { Event_Type } from "../../generated/core/v1/events";
import { type Balance, EntityKind, type State } from "../../generated/core/v1/financial_plan";
import { addMonths, compareDates, monthsBetween, yearFraction } from "../lib/dates";
import { actionsBetween } from "./actions";
import { incomeSummary } from "./cashflow";
import { type Plan, endDate, eventsOf, firstState, startDate, stateAt, yearlyStates } from "./core";

/**
 * Debt from the plan's debt balances, payment actions and DEBT_PAID_OFF events. Interest comes from
 * the engine's `interest_paid_to_date` on each debt balance; nothing is recomputed here.
 */
export type DebtSummary = {
  uuid: string;
  kind: EntityKind;
  balance: number;
  /** Paid toward it a month, averaged over the first year (all payment categories), or over the
   * months the plan covers when it stops sooner (it runs short). */
  monthlyPayment: number;
  /** Interest charged a month, averaged over the same months. */
  monthlyInterest: number;
  /** When the plan clears it (DEBT_PAID_OFF), if it does. */
  paidOff?: PDate;
};

const debtBalances = (s: State | undefined): Balance[] => s?.debts ?? [];

/** The largest interest_paid_to_date seen for each debt up to `until` (inclusive). */
function interestTo(plan: Plan, until?: PDate): Map<string, number> {
  const out = new Map<string, number>();
  plan?.monthlyStates.forEach((s) => {
    if (until && compareDates(s.date, until) > 0) return;
    s.debts.forEach((d) => {
      const v = d.interestPaidToDate ?? 0;
      if (v > (out.get(d.entityUuid) ?? 0)) out.set(d.entityUuid, v);
    });
  });
  return out;
}

export function paidOffDates(plan: Plan): Map<string, PDate> {
  const out = new Map<string, PDate>();
  eventsOf(plan, Event_Type.DEBT_PAID_OFF).forEach((e) => {
    if (e.entityUuid && e.date && !out.has(e.entityUuid)) out.set(e.entityUuid, e.date);
  });
  return out;
}

export function debtSummaries(plan: Plan, months = 12): DebtSummary[] {
  const s0 = firstState(plan);
  const from = startDate(plan);
  if (!s0 || !from) return [];
  const to = addMonths(from, months);
  // a plan that runs short stops there (its last state is that day): average over what it covers,
  // not the whole year, or every payment reads as a fraction of itself ($644 for a $966 payment)
  const stop = plan?.monthlyStates.at(-1)?.date;
  const span =
    stop && compareDates(stop, to) < 0
      ? Math.max(1, (yearFraction(stop) - yearFraction(from)) * 12)
      : months;
  const interest = interestTo(plan, to);
  const paid = paidOffDates(plan);
  const payments = new Map<string, number>();
  actionsBetween(plan, from, to)
    .filter(
      (f) =>
        f.kind === "moveMoneyFromChequingToDebtAction" &&
        (f.category === ActionCategory.DEBT_MINIMUM ||
          f.category === ActionCategory.DEBT_EXTRA ||
          f.category === ActionCategory.HOUSING ||
          f.category === ActionCategory.UNSPECIFIED),
    )
    .forEach((f) => payments.set(f.targetUuid, (payments.get(f.targetUuid) ?? 0) + f.amount));
  return debtBalances(s0)
    .filter((d) => d.balance > 0.5)
    .map((d) => ({
      uuid: d.entityUuid,
      kind: d.kind,
      balance: d.balance,
      monthlyPayment: (payments.get(d.entityUuid) ?? 0) / span,
      monthlyInterest: ((interest.get(d.entityUuid) ?? 0) - (d.interestPaidToDate ?? 0)) / span,
      paidOff: paid.get(d.entityUuid),
    }))
    .sort((a, b) => b.balance - a.balance);
}

/** When everything is cleared: the date the last debt balance reaches zero, if it does. */
export function debtFreeDate(plan: Plan, kinds?: Set<EntityKind>): PDate | undefined {
  if (!plan) return undefined;
  const states = plan.monthlyStates;
  const owes = (s: State) =>
    s.debts.filter((d) => !kinds || kinds.has(d.kind)).reduce((a, d) => a + d.balance, 0) > 0.5;
  if (!states.length) return undefined;
  let last = -1;
  states.forEach((s, i) => owes(s) && (last = i));
  if (last < 0) return states[0].date;
  if (last === states.length - 1) return undefined;
  const lo = states[last].date;
  const hi = states[last + 1].date;
  // The paid-off event is more precise than the month-end state when there is one.
  const evs = eventsOf(plan, Event_Type.DEBT_PAID_OFF).filter(
    (e) => compareDates(e.date, lo) > 0 && compareDates(e.date, hi) <= 0,
  );
  return evs.at(-1)?.date ?? hi;
}

/**
 * Interest over the whole plan (to its end), all debts; with `until`, only to the last month-end
 * before `until`'s month (as a what-if's comparison reads both plans).
 */
export function lifetimeInterest(plan: Plan, until?: PDate): number {
  const cut = until && { ...plan!, monthlyStates: statesBeforeMonthOf(plan, until) };
  let total = 0;
  interestTo(cut || plan, cut ? undefined : endDate(plan)).forEach((v) => (total += v));
  return total;
}

/** Month-end states before `until`'s month (the first state always kept). */
function statesBeforeMonthOf(plan: Plan, until: PDate): State[] {
  const monthStart = { year: until.year, month: until.month, day: 1 };
  return (plan?.monthlyStates ?? []).filter(
    (s, i) => i === 0 || compareDates(s.date, monthStart) < 0,
  );
}

/**
 * Where the Debt page's interest comparison stops: the earlier first shortfall of the two runs, as
 * the engine stops a plan there (so a run that stops pays no interest after it, and "over the life
 * of the debt" would be cut silently). Undefined when neither runs short.
 */
export function interestComparedUntil(abi: Plan, naive?: Plan): PDate | undefined {
  return [abi, naive]
    .map((p) => eventsOf(p, Event_Type.CASH_SHORTFALL)[0]?.date)
    .filter((x): x is PDate => !!x)
    .sort(compareDates)[0];
}

export type DebtComparison = {
  /** Debt payments as a share of take-home income, first year. */
  shareOfIncome?: number;
  monthlyPayment: number;
  monthsToDebtFree?: number;
  debtFree?: PDate;
  lifetimeInterest: number;
  totalOwed: number;
  monthlyInterest: number;
};

/** `until`: interest only up to then (`lifetimeInterest`); everything else from the whole plan. */
export function debtComparison(plan: Plan, until?: PDate): DebtComparison {
  const sums = debtSummaries(plan);
  const monthlyPayment = sums.reduce((s, d) => s + d.monthlyPayment, 0);
  const income = incomeSummary(plan).net;
  const from = startDate(plan);
  const free = debtFreeDate(plan);
  return {
    shareOfIncome: income > 0 ? monthlyPayment / income : undefined,
    monthlyPayment,
    debtFree: free,
    monthsToDebtFree: free && from ? Math.max(0, monthsBetween(from, free)) : undefined,
    lifetimeInterest: lifetimeInterest(plan, until),
    totalOwed: sums.reduce((s, d) => s + d.balance, 0),
    monthlyInterest: sums.reduce((s, d) => s + d.monthlyInterest, 0),
  };
}

/** Each debt's balance at the start of each year, for the stacked balance chart. */
export function debtBalanceRows(
  plan: Plan,
  uuids: string[],
  untilYear?: number,
): { year: number; values: Record<string, number>; total: number }[] {
  return yearlyStates(plan)
    .filter((s) => !untilYear || s.date!.year <= untilYear)
    .map((s) => {
      const values: Record<string, number> = {};
      uuids.forEach((u) => (values[u] = s.debts.find((d) => d.entityUuid === u)?.balance ?? 0));
      return {
        year: s.date!.year,
        values,
        total: s.debts.reduce((a, d) => a + d.balance, 0),
      };
    });
}

/** Total of the given debts at a date (for overlaying the other plan's path). */
export function debtTotalAt(plan: Plan, date: PDate, uuids?: string[]): number {
  const s = stateAt(plan, date);
  return (s?.debts ?? [])
    .filter((d) => !uuids || uuids.includes(d.entityUuid))
    .reduce((a, d) => a + d.balance, 0);
}

/** A debt's clear date as a fractional year (for the schedule bars), or the plan end. */
export function clearYear(plan: Plan, uuid: string): number | undefined {
  const d = paidOffDates(plan).get(uuid);
  if (d) return yearFraction(d);
  // No event: the first month-end state where it reads zero.
  const s = plan?.monthlyStates.find(
    (st, i) => i > 0 && (st.debts.find((x) => x.entityUuid === uuid)?.balance ?? 0) <= 0.5,
  );
  return s?.date ? yearFraction(s.date) : undefined;
}

/**
 * One debt's balance at the start and the end of each year on both plans (Debt's grouped "Your
 * debts, year by year"), from the plan's first state until the year after both have cleared it, or
 * the plans' end. A plan with no state that year (it stopped short) leaves its side out.
 */
export function debtYearRows(
  abi: Plan,
  naive: Plan,
  uuid: string,
): { year: number; label: string; plan?: number; now?: number }[] {
  const pick = (plan: Plan) => {
    const m = new Map<number, number>();
    yearlyStates(plan).forEach((s, i) =>
      m.set(i === 0 ? -1 : s.date!.year, s.debts.find((d) => d.entityUuid === uuid)?.balance ?? 0),
    );
    return m;
  };
  const a = pick(abi);
  const n = naive ? pick(naive) : undefined;
  const start = startDate(abi)?.year;
  if (start === undefined) return [];
  const years = [...new Set([...a.keys(), ...(n?.keys() ?? [])])]
    .filter((y) => y > 0)
    .sort((x, y) => x - y);
  const rows = [{ year: start, label: "Now", plan: a.get(-1), now: n?.get(-1) }];
  for (const y of years) {
    const prev = rows[rows.length - 1];
    if ((prev.plan ?? 0) <= 0.5 && (prev.now ?? 0) <= 0.5 && rows.length > 1) break;
    rows.push({ year: y, label: String(y), plan: a.get(y), now: n?.get(y) });
  }
  return rows;
}

/**
 * When a plan clears a debt: the DEBT_PAID_OFF date, else the first state that reads zero; `never`
 * when the plan runs to its end still owing it (derived from the plan itself, not from whether a
 * payment covers its interest). A plan that stops at a shortfall with the debt still owing says
 * neither: `cut`.
 */
export function debtClear(
  plan: Plan,
  uuid: string,
): { at?: number; date?: PDate; never: boolean; cut: boolean } {
  const date = paidOffDates(plan).get(uuid);
  if (date) return { at: yearFraction(date), date, never: false, cut: false };
  const s = plan?.monthlyStates.find(
    (st, i) => i > 0 && (st.debts.find((x) => x.entityUuid === uuid)?.balance ?? 0) <= 0.5,
  );
  if (s?.date) return { at: yearFraction(s.date), date: s.date, never: false, cut: false };
  const short = eventsOf(plan, Event_Type.CASH_SHORTFALL).length > 0;
  return { never: !short && !!plan?.monthlyStates.length, cut: short };
}
